import styles from './HomeBody.module.css';

import { useState, useEffect, useMemo, useRef, useContext } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { formatDate } from '../../Funcoes/FormatData';
import { AuthContext } from '../../Auth/AuthProvider';

import SortableTable from '../Utils/SortableTable';
import GenericHeader from '../GenericHeader';
import Filters from '../Utils/Filters';
import FilterContainer from '../Utils/FilterContainer';
import MultiselectCheck from '../Utils/MultiselectCheck';
import { VerifyPermission } from '../../Funcoes/VerifyPermission';

const Statistics = styled.div`
    width: 280px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ContainerStatistic = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 100%;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const HomeBody = ({ dados, funcoes, encarregados, obras, contratos, disciplinas = [], onFilter, loading, modulo }) => {

    const { user } = useContext(AuthContext);

    const navigate = (matricula) => {

        if (modulo === 'rh') {

            window.open(`/modulo/${modulo}/colaborador/${matricula}`, "_blank");
            return;

        }

        if (modulo === 'ssma' || modulo === 'qualidade') {

            if (VerifyPermission(user, { modulo, funcao: 'access_colab' })) {
                window.open(`/modulo/${modulo}/colaborador/${matricula}`, "_blank");
            } else {
                toast.error('Você não possui permissão para acessar a ficha do colaborador');
            }

        }
    }

    const tabelaRef = useRef(null);

    const total = dados.length;
    const ativos = dados.filter(obj => obj.STATUS === "ATIVO").length;
    const desligados = dados.filter(obj => obj.STATUS === "DESLIGADO").length;
    const afastados = dados.filter(obj => obj.STATUS === "AFASTADO").length;
    const ferias = dados.filter(obj => obj.STATUS === "FERIAS").length;
    const diretos = dados.filter(obj => obj.MAOOBRA === "D").length;
    const indiretos = dados.filter(obj => obj.MAOOBRA === "I").length;

    const [linhas, setLinhas] = useState(100)

    const [nomeColab, setNomeColab] = useState('')
    const [matColab, setMatColab] = useState('')

    const [selectedFunc, setSelectedFunc] = useState([])
    const [selectedEnc, setSelectedEnc] = useState([])
    const [selectedObra, setSelectedObra] = useState([])
    const [selectedContrato, setSelectedContrato] = useState([])
    const [selectedDisciplina, setSelectedDisciplina] = useState([])
    const [selectedStatus, setSelectedStatus] = useState([])
    const [selectedMaoobra, setSelectedMaoobra] = useState([])

    const opcoesFuncao = useMemo(() => funcoes.map(funcao => ({ value: funcao, label: funcao })), [funcoes]);
    const opcoesEncarregado = useMemo(() => encarregados.map(enc => ({ value: enc, label: enc })), [encarregados]);
    const opcoesObra = useMemo(() => obras.map(obra => ({ value: obra, label: obra })), [obras]);
    const opcoesContrato = useMemo(() => contratos.map(cont => ({ value: cont, label: cont })), [contratos]);
    const opcoesDisciplina = useMemo(() => disciplinas.map(disc => ({ value: disc, label: disc })), [disciplinas]);

    const opcoesStatus = useMemo(() => [
        { value: 'ATIVO', label: 'ATIVO' },
        { value: 'DESLIGADO', label: 'DESLIGADO' },
        { value: 'AFASTADO', label: 'AFASTADO' },
        { value: 'FERIAS', label: 'FÉRIAS' },
    ], []);

    const opcoesMaoobra = useMemo(() => [
        { value: 'D', label: 'DIRETA' },
        { value: 'I', label: 'INDIRETA' },
    ], []);

    useEffect(() => {

        onFilter(nomeColab, matColab, selectedFunc, selectedEnc, selectedObra, selectedContrato, selectedStatus, selectedMaoobra, selectedDisciplina)

    }, [nomeColab, matColab, selectedFunc, selectedEnc, selectedObra, selectedContrato, selectedStatus, selectedMaoobra, selectedDisciplina])

    useEffect(() => {

        const element = tabelaRef.current;

        if (!element) return;

        const handleScroll = () => {

            const { scrollTop, scrollHeight, clientHeight } = element;

            if (scrollTop + clientHeight >= scrollHeight - 10) {
                setLinhas((prev) => prev + 100);
            }

        };

        element.addEventListener("scroll", handleScroll);

        return () => {
            element.removeEventListener("scroll", handleScroll);
        };

    }, []);

    const viewColaboradores = () => {

        if (modulo === 'rh') {

            return true;

        } else if (modulo === 'ssma') {

            return VerifyPermission(user, { modulo: 'ssma', funcao: 'view_colab' });

        } else if (modulo === 'qualidade') {

            return VerifyPermission(user, { modulo: 'qualidade', funcao: 'view_colab' });

        }

        return false;

    }

    const columns = [
        {
            id: "pendencia",
            header: "",
            accessor: () => null,
            sortable: false,
            width: "36px",
            align: "center",
            render: (_value, row) => (
                <div className={row.DADOS === "PENDENTE" ? styles.colabPend : styles.colabOk} />
            ),
        },
        { id: "nome", header: "COLABORADOR", accessor: (r) => r.NOME },
        { id: "matricula", header: "MATRICULA", accessor: (r) => r.MATRICULA },
        { id: "funcao", header: "FUNÇÃO", accessor: (r) => r.FUNCAO },
        { id: "encarregado", header: "LÍDER IMEDIATO", accessor: (r) => r.IMEDIATO ?? "-" },
        { id: "admissao", header: "ADMISSÃO", accessor: (r) => r.ADMISSAO, sortValue: (r) => r.ADMISSAO, render: (v) => formatDate(v) },
        {
            id: "demissao",
            header: "DEMISSÃO",
            accessor: (r) => (r.DEMISSAO !== "        " ? r.DEMISSAO : null),
            sortValue: (r) => (r.DEMISSAO !== "        " ? r.DEMISSAO : null),
            render: (v) => (v ? formatDate(v) : "-"),
        },
        { id: "dias", header: "DIAS TRAB.", accessor: (r) => r.DIAS_TRAB, align: "left" },
        { id: "cc", header: "CENTRO CUSTO", accessor: (r) => r.CENTRO_CUSTO },
        { id: "contrato", header: "CONTRATO", accessor: (r) => r.CONTRATO ?? "-" },
        { id: "mo", header: "MO", accessor: (r) => r.MAOOBRA, width: "56px" },
        { id: "status", header: "STATUS", accessor: (r) => r.STATUS },
    ];

    return (
        <div className={styles.homeBody}>
            {
                viewColaboradores() && (
                    <>
                        <GenericHeader responsive>
                            <Filters columns={5}>
                                <FilterContainer
                                    type="text"
                                    placeholder="COLABORADOR..."
                                    valor={nomeColab}
                                    changeFunc={setNomeColab}
                                />
                                <FilterContainer
                                    type="text"
                                    placeholder="MATRICULA..."
                                    valor={matColab}
                                    changeFunc={setMatColab}
                                />
                                <MultiselectCheck
                                    options={opcoesFuncao}
                                    selectedOptions={selectedFunc}
                                    setSelectedOptions={setSelectedFunc}
                                    placeholder="FUNÇÃO..."
                                />
                                <MultiselectCheck
                                    options={opcoesEncarregado}
                                    selectedOptions={selectedEnc}
                                    setSelectedOptions={setSelectedEnc}
                                    placeholder="LÍDER IMEDIATO..."
                                />
                                <MultiselectCheck
                                    options={opcoesObra}
                                    selectedOptions={selectedObra}
                                    setSelectedOptions={setSelectedObra}
                                    placeholder="CENTRO CUSTO..."
                                />
                                <MultiselectCheck
                                    options={opcoesContrato}
                                    selectedOptions={selectedContrato}
                                    setSelectedOptions={setSelectedContrato}
                                    placeholder="CONTRATO..."
                                />
                                <MultiselectCheck
                                    options={opcoesDisciplina}
                                    selectedOptions={selectedDisciplina}
                                    setSelectedOptions={setSelectedDisciplina}
                                    placeholder="DISCIPLINA..."
                                />
                                <MultiselectCheck
                                    options={opcoesStatus}
                                    selectedOptions={selectedStatus}
                                    setSelectedOptions={setSelectedStatus}
                                    placeholder="STATUS..."
                                />
                                <MultiselectCheck
                                    options={opcoesMaoobra}
                                    selectedOptions={selectedMaoobra}
                                    setSelectedOptions={setSelectedMaoobra}
                                    placeholder="MÃO DE OBRA..."
                                />
                            </Filters>

                            <Statistics>
                                <ContainerStatistic>
                                    <TextoStatistic>COLABORADORES: {total}</TextoStatistic>
                                    <TextoStatistic>ATIVOS: {ativos}&nbsp;&nbsp;&nbsp;DESLIGADOS: {desligados}</TextoStatistic>
                                    <TextoStatistic>AFASTADOS: {afastados}&nbsp;&nbsp;&nbsp;FÉRIAS: {ferias}</TextoStatistic>
                                    <TextoStatistic>DIRETO: {diretos}&nbsp;&nbsp;&nbsp;INDIRETO: {indiretos}</TextoStatistic>
                                </ContainerStatistic>
                            </Statistics>
                        </GenericHeader>

                        <TableFlexFill>
                            <SortableTable
                                ref={tabelaRef}
                                columns={columns}
                                data={dados}
                                limit={linhas}
                                isLoading={loading}
                                loadingText="Carregando colaboradores..."
                                onRowClick={(row) => navigate(row.MATRICULA)}
                                rowCursor="pointer"
                                emptyText="NENHUM COLABORADOR ENCONTRADO"
                                initialSort={{ key: "nome", direction: "asc" }}
                            />
                        </TableFlexFill>
                    </>
                )
            }
        </div>

    )

}

export default HomeBody;
